import { useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { DataCard } from '@/components/card'
import { OpenDialogButton } from '@/components/ConsoleDialog/handlers'
import { DistributionRecipientForm } from '@/components/forms/DistributionRecipientForm'
import { DistributionRecipientsSportBulkEditForm } from '@/components/forms/DistributionRecipientsSportBulkEditForm/DistributionRecipientsSportBulkEditForm'
import { ActionDeleteItem, ActionsMenu } from '@/components/table/actions'
import { ActionFormItem } from '@/components/table/actions/ActionFormItem/ActionFormItem'
import { FilterableTable } from '@/components/table/base'
import { CheckboxMenu } from '@/components/table/checkbox'
import {
  CheckboxDeleteSelectedItem,
  CheckboxEditSelectedItems,
  CheckboxExportSelectedToCsvItem
} from '@/components/table/checkbox'
import { TitleSetter } from '@/components/Title'
import { createBulkHandler } from '@/service/bulkOperationsService'
import { deleteDistributionListRecipient } from '@/service/distributionListService'
import {
  type DistributionRecipientWithOperatorName,
  useDistributionRecipients
} from '@/stores/distributionRecipients'

import { DISTRIBUTION_LIST_COLUMNS, DISTRIBUTION_LIST_FILTERS } from './distribution.table'

const TABLE_ID = 'distribution-list-table'

export const DistributionListView = () => {
  const { distributionList, isFetching, isLoading, fetchData, resetState } =
    useDistributionRecipients(
      useShallow((state) => ({
        distributionList: state.data,
        isLoading: state.status === 'loading',
        isFetching: state.status === 'fetching',
        fetchData: state.fetchData,
        resetState: state.resetState
      }))
    )

  useEffect(() => {
    fetchData()
    return resetState
  }, [])

  const handleBulkDelete = createBulkHandler({
    name: 'Delete Recipients',
    operation: async (recipient: DistributionRecipientWithOperatorName) => {
      await deleteDistributionListRecipient({
        emailAddress: recipient.emailAddress
      })
    },
    refreshData: fetchData,
    getItemDisplayName: (recipient) => recipient.emailAddress
  })

  const handleDelete = async (recipient: DistributionRecipientWithOperatorName) => {
    await deleteDistributionListRecipient({
      emailAddress: recipient.emailAddress
    })
    fetchData()
  }

  return (
    <>
      <TitleSetter title="Distribution List" />

      <DataCard
        title="Distribution List"
        headerPostfixActions={
          <>
            <CheckboxMenu tableId={TABLE_ID}>
              <CheckboxExportSelectedToCsvItem
                csvHeaders={['Email', 'Sports', 'Role', 'Operator ID']}
                formatCsvRow={(row: DistributionRecipientWithOperatorName) => [
                  row.emailAddress,
                  row.sports?.join(',') || '',
                  row.recipientType,
                  row.operatorId?.toString() || ''
                ]}
              />
              <CheckboxEditSelectedItems
                getForm={(rows: DistributionRecipientWithOperatorName[]) => (
                  <DistributionRecipientsSportBulkEditForm
                    defaultValues={rows}
                    onSubmit={fetchData}
                  />
                )}
              />
              <CheckboxDeleteSelectedItem
                onDelete={handleBulkDelete}
                confirmationMessage="Are you sure you want to delete the following recipients?"
                confirmationTitle="Delete Recipients"
                successMessage="Recipients deleted successfully"
                errorMessage="Failed to delete recipients"
                renderItem={(row: DistributionRecipientWithOperatorName) => row.emailAddress}
              />
            </CheckboxMenu>
            <OpenDialogButton label="Add Recipient">
              <DistributionRecipientForm onSubmit={fetchData} />
            </OpenDialogButton>
          </>
        }
      >
        <FilterableTable
          tableId={TABLE_ID}
          data={distributionList}
          columns={DISTRIBUTION_LIST_COLUMNS}
          filters={DISTRIBUTION_LIST_FILTERS}
          defaultSort={{ attribute: 'emailAddress', order: 'asc' }}
          clientSideFilters
          clientSideSortAndPagination
          loading={isLoading}
          fetching={isFetching}
          withCheckboxes
          rowActions={
            <ActionsMenu>
              <ActionFormItem
                getForm={(row: DistributionRecipientWithOperatorName) => (
                  <DistributionRecipientForm defaultValues={row} onSubmit={fetchData} />
                )}
                isActive={(row: DistributionRecipientWithOperatorName) =>
                  row.recipientType === 'OPERATOR'
                }
                displaySeparately
              />
              <ActionDeleteItem
                onDelete={handleDelete}
                displaySeparately
                successMessage={(row: DistributionRecipientWithOperatorName) =>
                  `Recipient ${row.emailAddress} was deleted successfully`
                }
                errorMessage="Failed to delete recipient"
              />
            </ActionsMenu>
          }
        />
      </DataCard>
    </>
  )
}
